import React, { useState, useEffect, useRef } from 'react';
import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminDashboardHeader from './Components/AdminDashboardHeader';
import AdminDashboardSidebar from './Components/AdminDashboardSidebar';
import AdminDashboardContent from './Components/AdminDashboardContent';
import { Configuration } from '../../assets/data/config';
import Swal from 'sweetalert2';

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Get admin data from localStorage to determine default menu
  const adminDataStr = localStorage.getItem('adminData');
  const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;
  const usertype = adminData?.usertype;
  
  // Set default menu based on usertype
  const [selectedMenu, setSelectedMenu] = useState(usertype === 'Entity' ? 'Entity Management' : 'User Management');
  
  const sidebarRef = useRef(null);
  const headerRef = useRef(null);
  const navigate = useNavigate();
  
  // Session timeout management
  const sessionTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
  const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before timeout

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDesktopToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleMobileToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  // Handle logout functionality
  const handleLogout = async (showConfirmation = true) => {
    try {
      // Show confirmation dialog only if requested
      if (showConfirmation) {
        const result = await Swal.fire({
          title: 'Logout',
          text: 'Are you sure you want to logout?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, logout!',
          cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      // Show loading
      Swal.fire({
        title: 'Logging out...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Call logout API
      const response = await fetch(`${Configuration.apiService.key}admin/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        // Clear admin data
        localStorage.removeItem('adminData');
        localStorage.removeItem('isAdminLoggedIn');
        
        // Close loading and redirect based on usertype
        Swal.close();
        if (usertype === 'Entity') {
          navigate('/login');
        } else {
          navigate('/adminlogin');
        }
      } else {
        throw new Error('Logout API failed');
      }
    } catch (error) {
      console.error('Logout error:', error);
      
      // Close loading dialog if open
      Swal.close();
      
      // Even if API fails, clear local data and redirect
      localStorage.removeItem('adminData');
      localStorage.removeItem('isAdminLoggedIn');
      
      Swal.fire({
        title: 'Logged out',
        text: 'You have been logged out successfully.',
        icon: 'info',
        showConfirmButton: false,
        timer: 1500
      }).then(() => {
        if (usertype === 'Entity') {
          navigate('/login');
        } else {
          navigate('/adminlogin');
        }
      });
    }
  };

  // Handle session timeout
  const handleSessionTimeout = async () => {
    console.log('Session timeout - logging out admin');
    
    // Clear any existing timeouts
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    
    // Clear admin data
    localStorage.removeItem('adminData');
    localStorage.removeItem('isAdminLoggedIn');
    
    // Show timeout message
    await Swal.fire({
      title: 'Session Expired',
      text: 'Your session has expired due to inactivity. Please login again.',
      icon: 'warning',
      confirmButtonText: 'OK',
      allowOutsideClick: false
    });
    
    // Redirect to login based on usertype
    if (usertype === 'Entity') {
      navigate('/login');
    } else {
      navigate('/adminlogin');
    }
  };

  // Show warning before session timeout
  const showSessionWarning = async () => {
    const result = await Swal.fire({
      title: 'Session Expiring Soon',
      text: 'Your session will expire in 5 minutes due to inactivity. Click "Stay Active" to continue your session.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Stay Active',
      cancelButtonText: 'Logout Now',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      allowOutsideClick: false
    });

    if (result.isConfirmed) {
      // User wants to stay active - reset the timeout
      resetSessionTimeout();
    } else if (result.isDismissed) {
      // User wants to logout or dismissed - log them out
      await handleLogout(false);
    }
  };

  // Reset session timeout on user activity
  const resetSessionTimeout = () => {
    // Clear existing timeouts
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Set warning timeout (25 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      showSessionWarning();
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set session timeout (30 minutes)
    sessionTimeoutRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, SESSION_TIMEOUT);
  };

  // Redirect Entity users to Entity Dashboard
  useEffect(() => {
    if (usertype === 'Entity') {
      navigate('/admin/dashboard');
    }
  }, [usertype, navigate]);

  // Handle outside click to close sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on sidebar or header
      if (sidebarRef.current && sidebarRef.current.contains(event.target)) {
        return;
      }
      if (headerRef.current && headerRef.current.contains(event.target)) {
        return;
      }

      // Close sidebar on outside click
      if (mobileOpen && isMobile) {
        setMobileOpen(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen, mobileOpen, isMobile]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+Q for logout
      if (event.ctrlKey && event.key === 'q') {
        event.preventDefault();
        handleLogout();
      }
    };

    // Add keyboard event listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Session timeout management - track user activity
  useEffect(() => {
    console.log('Setting up session timeout for admin');
    
    // Activity events to track
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Activity handler
    const handleActivity = () => {
      resetSessionTimeout();
    };
    
    // Initialize session timeout
    resetSessionTimeout();
    
    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });
    
    // Cleanup function
    return () => {
      console.log('Cleaning up session timeout');
      
      // Remove event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      // Clear timeouts
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);

  // Handle menu selection with sidebar closing
  const handleMenuSelect = (menu) => {
    setSelectedMenu(menu);
    // Close sidebar on menu selection for mobile
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: '#F5F5F5' }}>
      <CssBaseline />

      <div ref={headerRef}>
        <AdminDashboardHeader
          onDrawerToggle={isMobile ? handleMobileToggle : handleDesktopToggle}
        />
      </div>

      <div ref={sidebarRef}>
        <AdminDashboardSidebar
          isMobile={isMobile}
          sidebarOpen={sidebarOpen}
          mobileOpen={mobileOpen}
          handleMobileToggle={handleMobileToggle}
          onMenuSelect={handleMenuSelect}
          selectedMenu={selectedMenu}
        />
      </div>

      <AdminDashboardContent selectedMenu={selectedMenu} sidebarOpen={sidebarOpen} />
    </Box>
  );
};

export default AdminDashboard;
